import { supabase } from '../lib/supabase';

/**
 * Product Service for Supabase CRUD operations and real-time subscriptions.
 * Handles products, variations, and kits with automatic case conversion.
 */

/**
 * Convert database row (snake_case) to React object (camelCase)
 */
function toReactFormat(dbRow) {
  if (!dbRow) return null;
  
  return {
    id: dbRow?.id,
    name: dbRow?.name,
    sku: dbRow?.sku,
    
    // Unified model fields (NEW)
    skuBase: dbRow?.sku_base,
    skuVariacao: dbRow?.sku_variacao,
    atributos: dbRow?.atributos || {},
    precoPorCanal: dbRow?.preco_por_canal || {},
    statusPorCanal: dbRow?.status_por_canal || {},
    
    productType: dbRow?.product_type,
    status: dbRow?.status,
    price: dbRow?.price,
    cost: dbRow?.cost,
    category: dbRow?.category,
    brand: dbRow?.brand,
    
    // Supplier fields (NEW)
    supplierName: dbRow?.supplier_name,
    supplierState: dbRow?.supplier_state,
    
    weight: dbRow?.weight,
    width: dbRow?.width,
    height: dbRow?.height,
    length: dbRow?.length,
    stock: dbRow?.stock,
    images: dbRow?.images || [],
    description: dbRow?.description,
    condition: dbRow?.condition,
    parentId: dbRow?.parent_id,
    source: dbRow?.source,
    
    // Marketplace fields
    ncm: dbRow?.ncm,
    ean: dbRow?.ean,
    cest: dbRow?.cest,
    estadoOrigem: dbRow?.estado_origem,
    shopeeCategory: dbRow?.shopee_category,
    mercadolivreCategory: dbRow?.mercadolivre_category,
    mercadolivreListingType: dbRow?.mercadolivre_listing_type,
    strategy: dbRow?.strategy,
    desiredMargin: dbRow?.desired_margin,
    
    // Channel readiness
    shopeeReady: dbRow?.shopee_ready,
    mercadolivreReady: dbRow?.mercadolivre_ready,
    
    // CSV tracking (NEW)
    csvImportId: dbRow?.csv_import_id,
    csvRowNumber: dbRow?.csv_row_number,
    csvOriginalData: dbRow?.csv_original_data,
    editedFields: dbRow?.edited_fields || {},
    
    metadata: dbRow?.metadata,
    createdAt: dbRow?.created_at,
    updatedAt: dbRow?.updated_at
  };
}

/**
 * Convert React object (camelCase) to database format (snake_case)
 */
function toDbFormat(reactObj) {
  const dbObj = {};
  
  if (reactObj?.name !== undefined) dbObj.name = reactObj?.name;
  if (reactObj?.sku !== undefined) dbObj.sku = reactObj?.sku;
  
  // Unified model fields (NEW)
  if (reactObj?.skuBase !== undefined) dbObj.sku_base = reactObj?.skuBase;
  if (reactObj?.skuVariacao !== undefined) dbObj.sku_variacao = reactObj?.skuVariacao;
  if (reactObj?.atributos !== undefined) dbObj.atributos = reactObj?.atributos;
  if (reactObj?.precoPorCanal !== undefined) dbObj.preco_por_canal = reactObj?.precoPorCanal;
  if (reactObj?.statusPorCanal !== undefined) dbObj.status_por_canal = reactObj?.statusPorCanal;
  
  if (reactObj?.productType !== undefined) dbObj.product_type = reactObj?.productType;
  if (reactObj?.status !== undefined) dbObj.status = reactObj?.status;
  if (reactObj?.price !== undefined) dbObj.price = reactObj?.price;
  if (reactObj?.cost !== undefined) dbObj.cost = reactObj?.cost;
  if (reactObj?.category !== undefined) dbObj.category = reactObj?.category;
  if (reactObj?.brand !== undefined) dbObj.brand = reactObj?.brand;
  
  // Supplier fields (NEW)
  if (reactObj?.supplierName !== undefined) dbObj.supplier_name = reactObj?.supplierName;
  if (reactObj?.supplierState !== undefined) dbObj.supplier_state = reactObj?.supplierState;
  
  if (reactObj?.weight !== undefined) dbObj.weight = reactObj?.weight;
  if (reactObj?.width !== undefined) dbObj.width = reactObj?.width;
  if (reactObj?.height !== undefined) dbObj.height = reactObj?.height;
  if (reactObj?.length !== undefined) dbObj.length = reactObj?.length;
  if (reactObj?.stock !== undefined) dbObj.stock = reactObj?.stock;
  if (reactObj?.images !== undefined) dbObj.images = reactObj?.images;
  if (reactObj?.description !== undefined) dbObj.description = reactObj?.description;
  if (reactObj?.condition !== undefined) dbObj.condition = reactObj?.condition;
  if (reactObj?.parentId !== undefined) dbObj.parent_id = reactObj?.parentId;
  if (reactObj?.source !== undefined) dbObj.source = reactObj?.source;
  
  // Marketplace fields
  if (reactObj?.ncm !== undefined) dbObj.ncm = reactObj?.ncm;
  if (reactObj?.ean !== undefined) dbObj.ean = reactObj?.ean;
  if (reactObj?.cest !== undefined) dbObj.cest = reactObj?.cest;
  if (reactObj?.estadoOrigem !== undefined) dbObj.estado_origem = reactObj?.estadoOrigem;
  if (reactObj?.shopeeCategory !== undefined) dbObj.shopee_category = reactObj?.shopeeCategory;
  if (reactObj?.mercadolivreCategory !== undefined) dbObj.mercadolivre_category = reactObj?.mercadolivreCategory;
  if (reactObj?.mercadolivreListingType !== undefined) dbObj.mercadolivre_listing_type = reactObj?.mercadolivreListingType;
  if (reactObj?.strategy !== undefined) dbObj.strategy = reactObj?.strategy;
  if (reactObj?.desiredMargin !== undefined) dbObj.desired_margin = reactObj?.desiredMargin;
  
  // Channel readiness
  if (reactObj?.shopeeReady !== undefined) dbObj.shopee_ready = reactObj?.shopeeReady;
  if (reactObj?.mercadolivreReady !== undefined) dbObj.mercadolivre_ready = reactObj?.mercadolivreReady;
  
  // CSV tracking (NEW)
  if (reactObj?.csvImportId !== undefined) dbObj.csv_import_id = reactObj?.csvImportId;
  if (reactObj?.csvRowNumber !== undefined) dbObj.csv_row_number = reactObj?.csvRowNumber;
  if (reactObj?.csvOriginalData !== undefined) dbObj.csv_original_data = reactObj?.csvOriginalData;
  if (reactObj?.editedFields !== undefined) dbObj.edited_fields = reactObj?.editedFields;
  
  if (reactObj?.metadata !== undefined) dbObj.metadata = reactObj?.metadata;
  
  return dbObj;
}

export const productService = {
  /**
   * Get all products from Supabase with pagination
   * @param {number} limit - Number of products per page (default: 20)
   * @param {number} offset - Number of products to skip (default: 0)
   * @returns {Promise<{products: Array, total: number}>}
   */
  async getAll(limit = null, offset = 0) {
    try {
      // Validate Supabase client is initialized
      if (!supabase) {
        throw new Error('Supabase client not initialized. Check environment variables.');
      }

      let query = supabase?.from('products')?.select('*', { count: 'exact' });
      
      if (limit) {
        query = query?.range(offset, offset + limit - 1);
      }
      
      query = query?.order('created_at', { ascending: false });
      
      const { data, error, count } = await query;
      
      if (error) {
        console.error('Supabase query error:', error);
        
        // Specific handling for schema cache errors
        if (error?.message?.includes('schema cache') || error?.code === 'PGRST002') {
          throw new Error('Database connection issue: The Supabase project may be paused or experiencing connectivity problems. Please check your project status at https://supabase.com/dashboard');
        }
        
        throw new Error(`Database error: ${error.message}`);
      }
      
      return {
        products: data?.map(toReactFormat) || [],
        total: count || 0
      };
    } catch (error) {
      console.error('Error fetching products:', error);
      
      // Provide more specific error messages
      if (error?.message?.includes('schema cache') || error?.message?.includes('PGRST002')) {
        throw new Error('Database connection issue: The Supabase project may be paused or experiencing connectivity problems. Please check your project status.');
      }
      
      if (error?.message?.includes('Failed to fetch')) {
        throw new Error('Unable to connect to database. Please check your internet connection and Supabase configuration.');
      }
      throw new Error(error.message || 'Failed to fetch products');
    }
  },

  /**
   * Get products by type (product, variation, kit)
   */
  async getByType(productType) {
    try {
      const { data, error } = await supabase?.from('products')?.select('*')?.eq('product_type', productType)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      return data?.map(toReactFormat) || [];
    } catch (error) {
      console.error('Error fetching products by type:', error);
      throw new Error(error.message || 'Failed to fetch products');
    }
  },

  /**
   * Get single product by ID
   */
  async getById(productId) {
    try {
      const { data, error } = await supabase?.from('products')?.select('*')?.eq('id', productId)?.single();
      
      if (error) throw error;
      return toReactFormat(data);
    } catch (error) {
      console.error('Error fetching product:', error);
      throw new Error(error.message || 'Failed to fetch product');
    }
  },

  /**
   * Create new product
   */
  async create(product) {
    try {
      const dbProduct = toDbFormat(product);
      const { data, error } = await supabase?.from('products')?.insert(dbProduct)?.select()?.single();
      
      if (error) throw error;
      return toReactFormat(data);
    } catch (error) {
      console.error('Error creating product:', error);
      throw new Error(error.message || 'Failed to create product');
    }
  },

  /**
   * Bulk create products (for CSV import)
   */
  async bulkCreate(products) {
    try {
      const dbProducts = products?.map(toDbFormat);
      const { data, error } = await supabase?.from('products')?.insert(dbProducts)?.select();
      
      if (error) throw error;
      return data?.map(toReactFormat) || [];
    } catch (error) {
      console.error('Error bulk creating products:', error);
      throw new Error(error.message || 'Failed to import products');
    }
  },

  /**
   * Update existing product with edit tracking
   * Tracks which fields were manually edited vs CSV original
   */
  async update(productId, updates) {
    try {
      // Get current product to track edits
      const current = await this.getById(productId);
      
      // Track edited fields
      const editedFields = current?.editedFields || {};
      Object.keys(updates)?.forEach(key => {
        if (key !== 'editedFields' && key !== 'updatedAt') {
          editedFields[key] = {
            editedAt: new Date()?.toISOString(),
            previousValue: current?.[key]
          };
        }
      });
      
      // CRITICAL: Preserve kit images and components
      if (current?.productType === 'kit') {
        // Preserve existing images if not explicitly updated
        if (!updates?.images || updates?.images?.length === 0) {
          updates.images = current?.images || [];
        }
        
        // Preserve kit components metadata
        if (!updates?.metadata?.kitComponents && current?.metadata?.kitComponents) {
          updates.metadata = {
            ...(updates?.metadata || {}),
            kitComponents: current?.metadata?.kitComponents
          };
        }
      }
      
      // Add edited fields tracking to updates
      const updatesWithTracking = {
        ...updates,
        editedFields
      };
      
      const dbUpdates = toDbFormat(updatesWithTracking);
      const { data, error } = await supabase?.from('products')?.update(dbUpdates)?.eq('id', productId)?.select()?.single();
      
      if (error) throw error;
      return toReactFormat(data);
    } catch (error) {
      console.error('Error updating product:', error);
      throw new Error(error.message || 'Failed to update product');
    }
  },

  /**
   * Delete product
   */
  async delete(productId) {
    try {
      const { error } = await supabase?.from('products')?.delete()?.eq('id', productId);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw new Error(error.message || 'Failed to delete product');
    }
  },

  /**
   * Clear all products (for re-import)
   */
  async clearAll() {
    try {
      const { error } = await supabase?.from('products')?.delete()?.neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all
      
      if (error) throw error;
    } catch (error) {
      console.error('Error clearing products:', error);
      throw new Error(error.message || 'Failed to clear products');
    }
  },
  
  /**
   * Bulk delete products with option to include kits
   * @param {boolean} includeKits - Whether to delete kits as well
   * @returns {Promise<{productsDeleted: number, kitsDeleted: number}>}
   */
  async bulkDeleteAll(includeKits = false) {
    try {
      let productsDeleted = 0;
      let kitsDeleted = 0;
      
      if (includeKits) {
        // Delete all products including kits
        const { data: allProducts, error: fetchError } = await supabase?.from('products')?.select('id, product_type');
        if (fetchError) throw fetchError;
        
        productsDeleted = allProducts?.filter(p => p?.product_type === 'product')?.length || 0;
        kitsDeleted = allProducts?.filter(p => p?.product_type === 'kit')?.length || 0;
        
        const { error: deleteError } = await supabase?.from('products')?.delete()?.neq('id', '00000000-0000-0000-0000-000000000000');
        if (deleteError) throw deleteError;
      } else {
        // Delete only products, keep kits
        const { data: products, error: fetchError } = await supabase?.from('products')?.select('id')?.eq('product_type', 'product');
        if (fetchError) throw fetchError;
        
        productsDeleted = products?.length || 0;
        
        const { error: deleteError } = await supabase?.from('products')?.delete()?.eq('product_type', 'product');
        if (deleteError) throw deleteError;
      }
      
      return { productsDeleted, kitsDeleted };
    } catch (error) {
      console.error('Error bulk deleting products:', error);
      throw new Error(error.message || 'Failed to bulk delete products');
    }
  },

  /**
   * Get unique categories from all products
   */
  async getCategories() {
    try {
      const { data, error } = await supabase?.from('products')?.select('category')?.not('category', 'is', null);
      
      if (error) throw error;
      
      const uniqueCategories = [...new Set(data?.map(p => p?.category)?.filter(c => c))]?.sort();
      return uniqueCategories;
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  },
  
  /**
   * Get unique brands from all products
   */
  async getBrands() {
    try {
      const { data, error } = await supabase?.from('products')?.select('brand')?.not('brand', 'is', null);
      
      if (error) throw error;
      
      const uniqueBrands = [...new Set(data?.map(p => p?.brand)?.filter(b => b))]?.sort();
      return uniqueBrands;
    } catch (error) {
      console.error('Error fetching brands:', error);
      return [];
    }
  },

  /**
   * Subscribe to real-time changes
   */
  subscribeToChanges(callback) {
    const channel = supabase?.channel('products_changes')?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'products' },
        (payload) => {
          const eventType = payload?.eventType;
          const product = payload?.new ? toReactFormat(payload?.new) : null;
          const oldProduct = payload?.old ? toReactFormat(payload?.old) : null;
          
          callback({ eventType, product, oldProduct });
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  },

  /**
   * Get kit items for a specific kit
   */
  async getKitItems(kitId) {
    try {
      const { data, error } = await supabase?.from('kit_items')?.select(`
          id,
          quantity,
          product:products!kit_items_product_id_fkey(*)
        `)?.eq('kit_id', kitId);
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching kit items:', error);
      throw new Error(error.message || 'Failed to fetch kit items');
    }
  },

  /**
   * Create kit with components
   * @param {Object} kitData - Kit product data
   * @param {Array} components - Array of {product_id, quantity}
   */
  async createKit(kitData, components) {
    try {
      // Fetch component products to get their CSV data and images
      let csvData = null;
      let allImages = [];
      
      if (components && components?.length > 0) {
        const componentIds = components?.map(c => c?.product_id || c?.id);
        const { data: componentProducts, error: fetchError } = await supabase
          ?.from('products')
          ?.select('csv_import_id, csv_row_number, csv_original_data, images')
          ?.in('id', componentIds);
        
        if (!fetchError && componentProducts?.length > 0) {
          // Collect all images from all components
          componentProducts?.forEach(product => {
            if (product?.images && Array.isArray(product?.images)) {
              allImages = [...allImages, ...product?.images];
            }
          });
          
          // Remove duplicates
          allImages = [...new Set(allImages)];
          
          // Store CSV data from first component that has it
          const productWithCsv = componentProducts?.find(p => p?.csv_original_data);
          if (productWithCsv) {
            csvData = {
              csv_import_id: productWithCsv?.csv_import_id,
              csv_original_data: productWithCsv?.csv_original_data
            };
          }
        }
      }

      // Create kit product with CSV data preserved and all images
      const dbKit = toDbFormat({
        ...kitData,
        productType: 'kit',
        status: 'draft',
        images: allImages?.length > 0 ? allImages : (kitData?.images || []),
        ...(csvData && {
          csvImportId: csvData?.csv_import_id,
          csvOriginalData: csvData?.csv_original_data
        })
      });
      
      const { data: kit, error: kitError } = await supabase?.from('products')?.insert(dbKit)?.select()?.single();
      
      if (kitError) throw kitError;
      
      // Create kit items
      if (components && components?.length > 0) {
        const kitItems = components?.map(comp => ({
          kit_id: kit?.id,
          product_id: comp?.product_id || comp?.id,
          quantity: comp?.quantity || 1
        }));
        
        const { error: itemsError } = await supabase?.from('kit_items')?.insert(kitItems);
        
        if (itemsError) throw itemsError;
      }
      
      return toReactFormat(kit);
    } catch (error) {
      console.error('Error creating kit:', error);
      throw new Error(error.message || 'Failed to create kit');
    }
  },

  /**
   * Update kit and its components
   */
  async updateKit(kitId, kitData, components) {
    try {
      // Update kit product
      const dbUpdates = toDbFormat(kitData);
      const { data: kit, error: kitError } = await supabase?.from('products')?.update(dbUpdates)?.eq('id', kitId)?.select()?.single();
      
      if (kitError) throw kitError;
      
      // Update kit items if provided
      if (components) {
        // Delete existing items
        await supabase?.from('kit_items')?.delete()?.eq('kit_id', kitId);
        
        // Insert new items
        if (components?.length > 0) {
          const kitItems = components?.map(comp => ({
            kit_id: kitId,
            product_id: comp?.product_id || comp?.id,
            quantity: comp?.quantity || 1
          }));
          
          const { error: itemsError } = await supabase?.from('kit_items')?.insert(kitItems);
          
          if (itemsError) throw itemsError;
        }
      }
      
      return toReactFormat(kit);
    } catch (error) {
      console.error('Error updating kit:', error);
      throw new Error(error.message || 'Failed to update kit');
    }
  },

  /**
   * Get kit with all components and their full CSV data
   */
  async getKitWithComponents(kitId) {
    try {
      const { data, error } = await supabase?.from('kit_items')?.select(`
          id,
          quantity,
          product:products!kit_items_product_id_fkey(*)
        `)?.eq('kit_id', kitId);

      if (error) throw error;

      const kit = await this.getById(kitId);
      
      // Map components with full CSV data inheritance
      const components = data?.map(item => ({
        id: item?.id,
        productId: item?.product?.id,
        quantity: item?.quantity,
        
        // Inherit all CSV fields from referenced product
        name: item?.product?.name,
        sku: item?.product?.sku,
        price: item?.product?.price,
        cost: item?.product?.cost,
        category: item?.product?.category,
        brand: item?.product?.brand,
        images: item?.product?.images || [],
        
        // CSV marketplace fields
        ean: item?.product?.ean,
        ncm: item?.product?.ncm,
        weight: item?.product?.weight,
        width: item?.product?.width,
        height: item?.product?.height,
        length: item?.product?.length,
        estadoOrigem: item?.product?.estado_origem,
        condition: item?.product?.condition,
        
        // Metadata fields
        supplierName: item?.product?.metadata?.supplier_name || '',
        supplierState: item?.product?.metadata?.supplier_state || '',
        cest: item?.product?.metadata?.cest || '',
        description: item?.product?.description || ''
      }));

      return {
        ...kit,
        components
      };
    } catch (error) {
      console.error('Error fetching kit with components:', error);
      throw new Error(error.message || 'Failed to fetch kit');
    }
  },

  /**
   * Add item to kit
   */
  async addKitItem(kitId, productId, quantity = 1) {
    try {
      const { data, error } = await supabase?.from('kit_items')?.insert({
          kit_id: kitId,
          product_id: productId,
          quantity
        })?.select()?.single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding kit item:', error);
      throw new Error(error.message || 'Failed to add kit item');
    }
  },

  /**
   * Remove item from kit
   */
  async removeKitItem(kitItemId) {
    try {
      const { error } = await supabase?.from('kit_items')?.delete()?.eq('id', kitItemId);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error removing kit item:', error);
      throw new Error(error.message || 'Failed to remove kit item');
    }
  }
};
